import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  GetResourceSearchResultsItem,
  GetResourceSearchResultsRequest,
} from "@/api/getResourceSearchResults/types";
import { AppDispatch, AppThunk } from "@/app/store";
import { partnerNetworkAlgoliaService } from "@/modules/hub/features/PartnerNetwork/services/partnerNetworkAlgoliaService";
import {
  Trade,
  BusinessRole,
} from "@/modules/hub/features/ResourceLibrary/api/getResourceLibraryMetadata/types";
import { ResourceCategory } from "@/modules/hub/features/ResourceLibrary/api/getResourceLibraryMetadata/types";

interface PartnerNetworkState {
  resources: GetResourceSearchResultsItem[];
  page: number;
  hasMore: boolean;
  totalCount: number;
  searchInput: string;
  showOnlyFavorites: boolean;
  showOnlyFeatured: boolean;
  scrollPosition: number;
  isRestoringState: boolean;
  isFetching: boolean;
  pendingRequest: "search" | "pagination" | null;
  lastFetchParams: {
    searchTerm?: string;
    showFavorites?: boolean;
    showFeatured?: boolean;
    resourceTypeIds?: number[];
    tradeIds?: number[];
    resourceCategoryIds?: number[];
    businessRoleIds?: number[];
    page?: number;
    pageSize?: number;
  } | null;

  selectedTrades: Trade[];
  selectedResourceCategories: ResourceCategory[];
  selectedBusinessRoles: BusinessRole[];

  // Dynamic facets from Algolia with counts
  availableFacets: {
    categories: Array<{ id: number; name: string; count: number }>;
    businessRoles: Array<{
      id: number;
      name: string;
      description: string | null;
      count: number;
    }>;
    trades: Array<{
      id: number;
      name: string;
      icon: string | null;
      count: number;
    }>;
  } | null;
}

const initialState: PartnerNetworkState = {
  resources: [],
  page: 1,
  hasMore: true,
  totalCount: 0,
  searchInput: "",
  showOnlyFavorites: false,
  showOnlyFeatured: false,
  scrollPosition: 0,
  isRestoringState: false,
  isFetching: false,
  pendingRequest: null,
  lastFetchParams: null,
  selectedTrades: [],
  selectedResourceCategories: [],
  selectedBusinessRoles: [],
  availableFacets: null,
};

const partnerNetworkSlice = createSlice({
  name: "partnerNetwork",
  initialState,
  reducers: {
    setResources: (
      state,
      action: PayloadAction<GetResourceSearchResultsItem[]>,
    ) => {
      state.resources = action.payload;
    },
    appendResources: (
      state,
      action: PayloadAction<GetResourceSearchResultsItem[]>,
    ) => {
      state.resources = [...state.resources, ...action.payload];
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setSearchInput: (state, action: PayloadAction<string>) => {
      state.searchInput = action.payload;
    },
    setIsFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    setPendingRequest: (
      state,
      action: PayloadAction<"search" | "pagination" | null>,
    ) => {
      state.pendingRequest = action.payload;
    },
    setAvailableFacets: (
      state,
      action: PayloadAction<PartnerNetworkState["availableFacets"]>,
    ) => {
      state.availableFacets = action.payload;
    },
    setShowOnlyFavorites: (state, action: PayloadAction<boolean>) => {
      state.showOnlyFavorites = action.payload;
    },
    setShowOnlyFeatured: (state, action: PayloadAction<boolean>) => {
      state.showOnlyFeatured = action.payload;
    },
    setScrollPosition: (state, action: PayloadAction<number>) => {
      state.scrollPosition = action.payload;
    },
    setIsRestoringState: (state, action: PayloadAction<boolean>) => {
      state.isRestoringState = action.payload;
    },
    setLastFetchParams: (
      state,
      action: PayloadAction<PartnerNetworkState["lastFetchParams"]>,
    ) => {
      state.lastFetchParams = action.payload;
    },
    setSelectedTrades: (state, action: PayloadAction<Trade[]>) => {
      state.selectedTrades = action.payload;
    },
    setSelectedResourceCategories: (
      state,
      action: PayloadAction<ResourceCategory[]>,
    ) => {
      state.selectedResourceCategories = action.payload;
    },
    setSelectedBusinessRoles: (
      state,
      action: PayloadAction<BusinessRole[]>,
    ) => {
      state.selectedBusinessRoles = action.payload;
    },
    resetState: (state) => {
      Object.assign(state, initialState);
    },
    clearFilters: (state) => {
      state.searchInput = "";
      state.showOnlyFavorites = false;
      state.showOnlyFeatured = false;
      state.selectedTrades = [];
      state.selectedResourceCategories = [];
      state.selectedBusinessRoles = [];
    },
  },
});

export const fetchResources =
  (params: GetResourceSearchResultsRequest): AppThunk =>
  async (dispatch: AppDispatch, getState) => {
    const state = getState().partnerNetwork;

    // Prevent concurrent search requests
    if (state.pendingRequest === "search") {
      return;
    }

    dispatch(setIsFetching(true));
    dispatch(setPendingRequest("search"));

    try {
      const result = await partnerNetworkAlgoliaService.searchPartners(params);

      dispatch(setResources(result.resources));
      dispatch(setTotalCount(result.totalCount));
      dispatch(setAvailableFacets(result.facets));
      dispatch(setPage(params.page || 1));
      dispatch(setLastFetchParams(params));

      const hasMore = result.resources.length >= (params.pageSize || 10);
      dispatch(setHasMore(hasMore));
    } catch (error) {
      console.error("Error fetching partners:", error);
      dispatch(setHasMore(false));
    } finally {
      dispatch(setIsFetching(false));
      dispatch(setPendingRequest(null));
    }
  };

export const fetchMoreResources =
  (params: GetResourceSearchResultsRequest): AppThunk =>
  async (dispatch: AppDispatch, getState) => {
    const state = getState().partnerNetwork;

    // Prevent concurrent pagination requests
    if (state.pendingRequest === "pagination") {
      return;
    }

    dispatch(setIsFetching(true));
    dispatch(setPendingRequest("pagination"));

    try {
      const result = await partnerNetworkAlgoliaService.searchPartners(params);

      dispatch(appendResources(result.resources));
      dispatch(setPage(params.page || 1));
      dispatch(setLastFetchParams(params));

      const hasMore = result.resources.length >= (params.pageSize || 10);
      dispatch(setHasMore(hasMore));
    } catch (error) {
      console.error("Error loading more partners:", error);
      dispatch(setHasMore(false));
    } finally {
      dispatch(setIsFetching(false));
      dispatch(setPendingRequest(null));
    }
  };

export const {
  setResources,
  appendResources,
  setPage,
  setHasMore,
  setTotalCount,
  setSearchInput,
  setShowOnlyFavorites,
  setShowOnlyFeatured,
  setScrollPosition,
  setLastFetchParams,
  setSelectedTrades,
  setSelectedResourceCategories,
  setSelectedBusinessRoles,
  setIsRestoringState,
  setIsFetching,
  setPendingRequest,
  setAvailableFacets,
  clearFilters,
} = partnerNetworkSlice.actions;

export default partnerNetworkSlice.reducer;
